// utils
import { parseBackendDateTime, toDateString } from 'utils/date';
import { isDayCovered } from './overlapPeriods';

// types
import type { AbsenceRolloutDutyLine } from 'RestAPIs/roster/register-absence';
import type {
  ConsequenceKind,
  ConsequenceRowModel,
  ConsequenceSummary,
  ConsequenceWorkAgreement,
  NoOpenDutyReason,
  PlanUnitGroup,
  PlanUnitRole
} from './ConsequencesPanel.types';
import type { CoveredAbsencePeriod } from './overlapPeriods';

type KindOptions = {
  selectedWaIds: number[];
  isEdit?: boolean;
  covered?: CoveredAbsencePeriod;
};

type OpenDutyOptions = {
  detachedGivesOpenDuty?: boolean;
  isEdit?: boolean;
};

type GroupOptions = {
  existingDutyLines: AbsenceRolloutDutyLine[];
  newDutyLines: AbsenceRolloutDutyLine[];
  selectedWaIds: number[];
  workAgreements: ConsequenceWorkAgreement[];
  coveredPeriods: CoveredAbsencePeriod[];
  isEdit?: boolean;
};

const startOf = (line: AbsenceRolloutDutyLine) =>
  parseBackendDateTime(line.startTime);

/**
 * The already-registered absence this line falls inside, if any. Matched on
 * the work agreement as well as the day: a second work agreement may well be
 * working while the first one is sick.
 */
export const coveredBy = (
  line: AbsenceRolloutDutyLine,
  coveredPeriods: CoveredAbsencePeriod[]
) =>
  coveredPeriods.find(
    (period) =>
      !!line.waId &&
      period.waIds.includes(line.waId) &&
      isDayCovered(startOf(line), [period])
  );

export const kindOf = (
  line: AbsenceRolloutDutyLine,
  isNew: boolean,
  { selectedWaIds, isEdit, covered }: KindOptions
): ConsequenceKind => {
  // editing has no work agreement choice: every duty belongs to the absence
  if (!isNew && !isEdit && (!line.waId || !selectedWaIds.includes(line.waId))) {
    return 'untouched';
  }
  // the day already belongs to another absence, so nothing here happens —
  // whether this row is a planned duty or one the rollout proposes
  if (covered) {
    return 'covered';
  }
  if (isNew) {
    return 'new';
  }
  // SKIP = the backend leaves this duty as it is (partial sickness)
  if (line.action === 'SKIP') {
    return 'unchanged';
  }
  return line.action === 'DELETE' ? 'detached' : 'modified';
};

/**
 * Whether an open duty can come back from this row at all: only a planned duty
 * that is converted or detached. When editing, a duty shrunk out of the period
 * is deleted outright, so only a type conversion detaches there — and a row is
 * only `modified` while editing when the planner picked another type (it is
 * `unchanged` otherwise), so a change to a detaching type is exactly what
 * makes it apply (the backend unlinks in `UpdateDutyHourTypeOperation`).
 */
export const openDutyApplies = (kind: ConsequenceKind, isEdit?: boolean) =>
  kind === 'modified' || (kind === 'detached' && !isEdit);

/** the original duty comes back as an open duty, to be refilled — but only
 *  when the duty carries a request (the backend unlinks per duty and skips
 *  duties without one) */
export const givesOpenDuty = (
  line: AbsenceRolloutDutyLine,
  kind: ConsequenceKind,
  { detachedGivesOpenDuty, isEdit }: OpenDutyOptions
) =>
  !!detachedGivesOpenDuty && !!line.requestId && openDutyApplies(kind, isEdit);

/**
 * Why a row gives no open duty. Every row answers the "Ontkoppeld" question:
 * a row that could detach does not when the duty hour type does not detach
 * or there is no request to detach from; every other row has nothing to
 * detach, each for its own reason.
 */
export const noOpenDutyReason = (
  kind: ConsequenceKind,
  // the column is not shown while editing, so only the new notice asks
  { detachedGivesOpenDuty }: Omit<OpenDutyOptions, 'isEdit'>
): NoOpenDutyReason => {
  if (openDutyApplies(kind)) {
    return detachedGivesOpenDuty ? 'noRequest' : 'typeDoesNotDetach';
  }
  switch (kind) {
    case 'new':
      return 'newRegistration';
    case 'covered':
      return 'covered';
    case 'untouched':
      return 'untouched';
    default:
      return 'unchanged';
  }
};

const byStart = (a: ConsequenceRowModel, b: ConsequenceRowModel) =>
  startOf(a.line).getTime() - startOf(b.line).getTime();

/** zebra per day: every day switches the background, rows of one day share it */
const withDayShading = (rows: ConsequenceRowModel[]) => {
  let dayIndex = -1;
  let previousDay = '';

  return rows.map((row) => {
    const day = toDateString(startOf(row.line));
    if (day !== previousDay) {
      dayIndex += 1;
      previousDay = day;
    }
    return { ...row, shaded: dayIndex % 2 === 1 };
  });
};

const uniqueNrs = (nrs: (number | string | undefined)[]) => [
  ...new Set(nrs.filter((nr) => nr !== undefined && nr !== '').map(String))
];

/**
 * The consequences as the planner reads them: per plan unit, then per day.
 * First the plan units of the checked work agreements (also those where the
 * employee was not planned at all), then the plan units the employee was
 * deployed on from there, and last — greyed — the duties of unchecked work
 * agreements, which this save leaves alone.
 *
 * Nothing at all to show gives no groups: the empty-result warning speaks
 * then, and a row of "not planned" headers would only repeat it.
 */
export const groupConsequences = ({
  existingDutyLines,
  newDutyLines,
  selectedWaIds,
  workAgreements,
  coveredPeriods,
  isEdit
}: GroupOptions): PlanUnitGroup[] => {
  const workAgreementById = new Map(
    workAgreements.map((workAgreement) => [workAgreement.waId, workAgreement])
  );
  const selectedWorkAgreements = workAgreements.filter((workAgreement) =>
    selectedWaIds.includes(workAgreement.waId)
  );
  const ownPlanUnits = new Set(
    selectedWorkAgreements
      .map((workAgreement) => workAgreement.planUnit)
      .filter((planUnit): planUnit is string => !!planUnit)
  );

  const nrOf = (line: AbsenceRolloutDutyLine) =>
    (line.waId ? workAgreementById.get(line.waId)?.nr : undefined) ?? line.waNr;

  // the duty's own plan unit; a proposed duty carries the one of its
  // inschaling, and without either the work agreement's is the best guess
  const planUnitOf = (line: AbsenceRolloutDutyLine) =>
    line.costCenterFullName ||
    (line.waId ? workAgreementById.get(line.waId)?.planUnit : undefined) ||
    '';

  const rows: ConsequenceRowModel[] = [
    ...existingDutyLines.map((line, index) => ({
      line,
      isNew: false,
      key: `existing-${line.id ?? index}`
    })),
    ...newDutyLines.map((line, index) => ({
      line,
      isNew: true,
      key: `new-${index}`
    }))
  ].map(({ line, isNew, key }) => {
    const covered = coveredBy(line, coveredPeriods);
    return {
      key,
      line,
      isNew,
      covered,
      kind: kindOf(line, isNew, { selectedWaIds, isEdit, covered }),
      shaded: false
    };
  });

  if (!rows.length) {
    return [];
  }

  const groups = new Map<string, PlanUnitGroup>();
  const groupFor = (planUnit: string, role: PlanUnitRole) => {
    const key = `${role === 'untouched' ? 'untouched' : 'active'}:${planUnit}`;
    const existing = groups.get(key);
    if (existing) {
      return existing;
    }
    const group: PlanUnitGroup = {
      key,
      planUnit,
      role,
      ownNrs:
        role === 'own'
          ? uniqueNrs(
              selectedWorkAgreements
                .filter((workAgreement) => workAgreement.planUnit === planUnit)
                .map((workAgreement) => workAgreement.nr)
            )
          : [],
      deployedNrs: [],
      rows: [],
      showsNoDutiesAdvice: false
    };
    groups.set(key, group);
    return group;
  };

  // the checked work agreements' own plan units come first, in their order,
  // and stand there even when the employee was not planned on them
  selectedWorkAgreements.forEach((workAgreement) => {
    if (workAgreement.planUnit) {
      groupFor(workAgreement.planUnit, 'own');
    }
  });

  rows.forEach((row) => {
    const planUnit = planUnitOf(row.line);
    if (row.kind === 'untouched') {
      const group = groupFor(planUnit, 'untouched');
      group.rows.push(row);
      // the unchecked work agreements these duties belong to
      group.ownNrs = uniqueNrs([...group.ownNrs, nrOf(row.line)]);
      return;
    }
    const group = groupFor(
      planUnit,
      ownPlanUnits.has(planUnit) ? 'own' : 'deployed'
    );
    group.rows.push(row);

    // deployed from a work agreement that does not hang on this plan unit.
    // A proposed registration was not planned anywhere, so it is never
    // "deployed": it goes to its inschaling's plan unit and says no more
    const workAgreement = row.line.waId
      ? workAgreementById.get(row.line.waId)
      : undefined;
    if (
      !row.isNew &&
      !workAgreement?.planUnitMayChange &&
      workAgreement?.planUnit !== planUnit
    ) {
      group.deployedNrs = uniqueNrs([...group.deployedNrs, nrOf(row.line)]);
    }
  });

  const firstStart = (group: PlanUnitGroup) =>
    group.rows.length
      ? Math.min(...group.rows.map((row) => startOf(row.line).getTime()))
      : Number.POSITIVE_INFINITY;
  const rank: Record<PlanUnitRole, number> = {
    own: 0,
    deployed: 1,
    untouched: 2
  };
  const ordered = [...groups.values()];
  const insertion = new Map(ordered.map((group, index) => [group.key, index]));

  return (
    ordered
      // own plan units keep the order of the work agreements; the others
      // follow in the order their first duty happens
      .sort((a, b) =>
        rank[a.role] !== rank[b.role]
          ? rank[a.role] - rank[b.role]
          : a.role === 'own'
          ? (insertion.get(a.key) ?? 0) - (insertion.get(b.key) ?? 0)
          : firstStart(a) - firstStart(b)
      )
      .map((group) => {
        const ownWaIds = selectedWorkAgreements
          .filter((workAgreement) => workAgreement.planUnit === group.planUnit)
          .map((workAgreement) => workAgreement.waId);
        return {
          ...group,
          rows: withDayShading([...group.rows].sort(byStart)),
          showsNoDutiesAdvice:
            group.role === 'own' &&
            group.rows.length === 0 &&
            !rows.some(
              (row) => !!row.line.waId && ownWaIds.includes(row.line.waId)
            )
        };
      })
  );
};

/** the totals above the table; unchecked work agreements are not part of it */
export const summarize = (
  groups: PlanUnitGroup[],
  options: OpenDutyOptions
): ConsequenceSummary =>
  groups
    .flatMap((group) => group.rows)
    .reduce<ConsequenceSummary>(
      (summary, row) => ({
        modified: summary.modified + (row.kind === 'modified' ? 1 : 0),
        detached: summary.detached + (row.kind === 'detached' ? 1 : 0),
        created: summary.created + (row.kind === 'new' ? 1 : 0),
        openDuties:
          summary.openDuties +
          (givesOpenDuty(row.line, row.kind, options) ? 1 : 0)
      }),
      { modified: 0, detached: 0, created: 0, openDuties: 0 }
    );
